<template>
  <div class="relative flex w-full flex-col gap-2">
    <Input
      v-model="query"
      type="text"
      placeholder="Введите адрес"
      :loading="isLoading"
      @keydown.arrow-up.prevent="blockArrow"
      @keydown.arrow-down.prevent="blockArrow"
      @keyup="onKeyPressed"
      class="w-full"
    />

    <div
      v-if="suggestions.length"
      class="ring-accented absolute top-full left-0 z-50 mt-1 flex flex-col rounded-md bg-white p-2 ring"
    >
      <div v-for="(suggestion, index) in suggestions" :key="index">
        <span
          :class="testActive(index)"
          class="hover:bg-accented flex w-full cursor-pointer truncate rounded-md px-2 text-left"
          @click="selectAddress(suggestion.label)"
        >
          {{ suggestion.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { useDebounceFn } from '@vueuse/core'
  import { uColor } from '~/helpers/colors'

  interface ISuggestion {
    value: string
    label: string
  }

  const query = defineModel<string>()

  const config = useRuntimeConfig()

  const suggestions = ref<ISuggestion[]>([])
  const isLoading = ref(false)
  const skipNextDebounce = ref(false)
  const apiKey = config.public.DADATA_API_KEY
  const apiBaseUrl = config.public.DADATA_BASE_URL

  const selected = ref(0)
  const selectedOption = computed<ISuggestion | null>(() =>
    suggestions.value.length ? suggestions.value?.[selected.value] : null
  )
  const testActive = (index: number): string => {
    return selected.value === index ? uColor('bg-primary-200') : ''
  }

  const debouncedFetch = useDebounceFn(async () => {
    if (!query.value?.trim()) {
      suggestions.value = []
      return
    }

    isLoading.value = true

    try {
      const response = await fetch(`${apiBaseUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Token ${apiKey}`,
        },
        body: JSON.stringify({
          query: query.value,
        }),
      })

      const data = await response.json()
      suggestions.value = data.suggestions.map((item: ISuggestion) => ({
        label: item.value,
        value: item.value,
      }))
      selected.value = 0
    } catch (error) {
      console.error('Ошибка запроса:', error)
      suggestions.value = []
    } finally {
      isLoading.value = false
    }
  }, 500)

  const selectAddress = (label: string) => {
    skipNextDebounce.value = true
    query.value = label
    suggestions.value = []
  }
  function blockArrow(e: KeyboardEvent) {
    e.preventDefault()
  }
  const onKeyPressed = (e: KeyboardEvent) => {
    const target = e.target as HTMLInputElement
    const code = e.code
    if (code === 'ArrowDown') {
      selected.value++
      if (selected.value > suggestions.value.length - 1) {
        selected.value = 0
      }
    }
    if (code === 'ArrowUp') {
      selected.value--
      if (selected.value < 0) {
        selected.value = suggestions.value.length - 1
      }
    }
    if (code === 'Enter' && selectedOption.value) {
      query.value = selectedOption.value?.label
      if (selectedOption.value?.label === target.value) {
        selectAddress(selectedOption.value?.label)
      } else {
        debouncedFetch()
      }
    }
  }

  watch(query, (newVal, oldVal) => {
    if (skipNextDebounce.value) {
      skipNextDebounce.value = false
      return
    }

    if (newVal !== oldVal) {
      debouncedFetch()
    }
  })
</script>
